import { Button, IconButton, Stack, Typography } from '@mui/material';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { AuditDetailsRoutes } from '@utils/routes.utils';

import { ArrowBack } from '@icons/index';

import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import RoutesTabsComponent from '@components/tabs/RoutesTabs.component';
import { Download } from '@mui/icons-material';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { useObservable } from '@ngneat/react-rxjs';
import NewAuditModal from '@screens/admin/audits/modal/NewAuditModal';
import { auditDetailsQuery } from '@store/audits/audit-details/audits-details.query';
import { auditDetailsService } from '@store/audits/audit-details/audits-details.service';
import { auditsOperationQuery } from '@store/audits/audits-operation/audit-operation.query';
import { auditsOperationService } from '@store/audits/audits-operation/audits-operation.service';
import { templatesQuery } from '@store/audits/templates/templates.query';
import { templatesService } from '@store/audits/templates/templates.service';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface AuditDetailsScreenProps {
    isTemplate?: boolean;
}

const AuditDetailsScreen = (props: AuditDetailsScreenProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { auditId } = useParams();
    const { templateId } = useParams();
    const { isTemplate } = props;
    const [isUpdate, setIsUpdate] = useState<boolean>(false);

    const [{ auditDetails }] = useObservable(auditDetailsQuery.auditDetails$);
    const [{ auditTemplateDetails }] = useObservable(templatesQuery.auditTemplateDetails$);
    const [filters] = useObservable(auditsOperationQuery.filters$);

    const handleClose = () => {
        navigate('..', { replace: true });
    };

    let routes = AuditDetailsRoutes.routes();

    if (isTemplate) {
        routes = routes.filter((r) => r.path !== AuditDetailsRoutes.OFFER && r.path !== AuditDetailsRoutes.PRECO);
    }

    if (!auditDetails.clientOrganisationUnitId) {
        const allowedRoutes = [AuditDetailsRoutes.PRE_AUDIT, AuditDetailsRoutes.AUDIT, AuditDetailsRoutes.PRECO];
        routes = routes.map((r) => allowedRoutes.includes(r.path) ? r : { ...r, disabled: true });
    }


    useEffect(() => {
        if (auditId && !isTemplate) {
            auditDetailsService.getAuditById(auditId).subscribe();
        }

        if (templateId && isTemplate) {
            templatesService.getTemplateById(templateId).subscribe();
        }

        return () => {
            auditDetailsService.resetStore();
        };
    }, [auditId, isTemplate, templateId]);

    return (
        <Stack flex={1} width="100%" pt="34px" px="24px" spacing={3} overflow="hidden">
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} ml="-9px">
                <Stack direction="row" spacing={1} alignItems="flex-start">
                    <IconButton onClick={handleClose}>
                        <ArrowBack />
                    </IconButton>
                    <Stack>
                        <Typography fontSize="30px" fontWeight="700">
                            {!isTemplate ? `Audit ${auditDetails?.name}` : `Template ${auditTemplateDetails?.name}`}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <Typography fontSize="15px" fontWeight="100">
                                {auditDetails?.clientOrganisationUnit}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
                {!isTemplate && (
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined" color="primary"
                            disabled={false}
                            startIcon={<Download />}>
                            {t('global.exportCSV')}
                        </Button>
                        <Button
                            variant="outlined" color="primary"
                            disabled={false}
                            onClick={() => setIsUpdate(true)}
                            startIcon={<BorderColorIcon />}>
                            {t('global.modifyInfos')}
                        </Button>
                    </Stack>
                )}
                {isUpdate && (<NewAuditModal isUpdate audit={auditDetails} handleClose={() => setIsUpdate(false)} />)}
            </Stack>
            <Stack flex={1} spacing={2} overflow="hidden">
                <Stack flex={1} overflow="hidden">
                    <Stack justifyContent="space-between" flexWrap="nowrap" flexDirection="row">
                        <Stack>
                            <RoutesTabsComponent routes={routes} />
                        </Stack>
                        <TableFiltersComponent
                            filters={filters}
                            handleSave={auditsOperationService.setFilters('tableFilters')}
                            handleReset={() => auditsOperationService.resetFilters()} />
                    </Stack>
                    <Outlet />
                </Stack>
            </Stack>
        </Stack>
    );
};

export default AuditDetailsScreen;
